import { useState } from 'react';

import { AppIcon, GeneratedContent } from '@backrs_ui/common-ui';

import { handleShareWithNavigator } from '@backrs_ui/common-ui/src/molecules/Nugget/DialogNuggetHeader';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  deleteContentNuggetBookmark,
  postContentNuggetBookmark,
} from '@backrs_ui/grow-ui/src/redux/user/thunks/nuggetBookmark';
import { selectBookmarkedNuggetId } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Theme, SxProps, Tooltip } from '@mui/material';

const MAKE_IT_PERSONAL_STRINGS = appStrings.modules.makeItPersonal;

const GROW_URL = process.env.REACT_APP_GROW_URL;

type ActionButtonsProps = Readonly<{
  generatedContent?: GeneratedContent;
}>;

export const ActionButtons = ({ generatedContent }: ActionButtonsProps) => {
  const dispatch = useAppDispatch();
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const bookmarkId = useAppSelector(
    selectBookmarkedNuggetId({
      nuggetId: generatedContent?.id,
      variant: 'generated-content',
    }),
  );

  const handleCopy = async () => {
    try {
      if (generatedContent?.content) {
        await navigator.clipboard.writeText(generatedContent.content);
        setShowCopySuccess(true);

        setTimeout(() => {
          setShowCopySuccess(false);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const handleBookmark = () => {
    if (generatedContent?.id) {
      if (bookmarkId) {
        dispatch(deleteContentNuggetBookmark(bookmarkId));
      } else {
        dispatch(
          postContentNuggetBookmark({
            type: 'generated-content',
            itemId: generatedContent.id,
          }),
        );
      }
    }
  };

  const handleShare = () => {
    const majorSourceDisplay = generatedContent?.major_source;

    handleShareWithNavigator({
      title: generatedContent?.minor_source
        ? `${majorSourceDisplay} - ${generatedContent.minor_source}`
        : majorSourceDisplay || '',
      text: generatedContent?.title || '',
      url: `${GROW_URL}/generated-content/${generatedContent?.uuid}`,
    });
  };

  return (
    <Box sx={iconsContainerSx}>
      <Tooltip
        open={showCopySuccess}
        title={MAKE_IT_PERSONAL_STRINGS.copied}
        placement="right"
        arrow
        componentsProps={{
          popper: {
            sx: tooltipPopperSx,
          },
        }}
      >
        <Box onClick={handleCopy} sx={iconContainerSx}>
          <AppIcon iconName="Copy02" diameter="20px" color="Colors/Foreground/fg-secondary" />
        </Box>
      </Tooltip>
      <Box onClick={handleBookmark} sx={iconContainerSx}>
        <AppIcon
          iconName="Bookmark"
          diameter="20px"
          fill={bookmarkId ? 'Colors/Foreground/fg-secondary' : undefined}
          color="Colors/Foreground/fg-secondary"
        />
      </Box>
      <Box onClick={handleShare} sx={iconContainerSx}>
        <AppIcon iconName="Send01" diameter="20px" color="Colors/Foreground/fg-secondary" />
      </Box>
    </Box>
  );
};

const iconsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  marginTop: backrs.spacing['spacing-lg'],
});

const iconContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const tooltipPopperSx: SxProps<Theme> = {
  zIndex: 99999999999999,
};
